/**
 * WorkspaceSwitcher.js — RC 1.30.B0 sidebar switcher.
 *
 * Compact Select dropdown, rendered only when the user has ≥2
 * allowed workspaces. Picking a workspace navigates to its landing
 * path AND auto-persists the new default via the WorkspaceContext.
 */
import { Briefcase, Building2, Shield } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useWorkspace } from '@/contexts/WorkspaceContext';

const LABELS = {
  candidate: { label: 'Candidate', Icon: Briefcase },
  employer:  { label: 'Employer',  Icon: Building2 },
  admin:     { label: 'Admin',     Icon: Shield },
};

export default function WorkspaceSwitcher({ collapsed = false }) {
  const { allowedWorkspaces, currentWorkspace, switchWorkspace } = useWorkspace();

  if (!allowedWorkspaces || allowedWorkspaces.length < 2) return null;
  const current = currentWorkspace || allowedWorkspaces[0];
  const curMeta = LABELS[current] || LABELS.candidate;
  const CurIcon = curMeta.Icon;

  if (collapsed) {
    // Render a single icon button that cycles to the next workspace
    // so the dropdown doesn't overflow the narrow sidebar.
    const next = allowedWorkspaces[
      (allowedWorkspaces.indexOf(current) + 1) % allowedWorkspaces.length
    ];
    return (
      <button
        type="button"
        onClick={() => switchWorkspace(next)}
        className="mx-auto mb-3 flex h-9 w-9 items-center justify-center rounded-md border border-ndor-edge bg-ndor-surface-alt hover:bg-ndor-surface text-ndor-ink"
        data-testid="workspace-switcher-collapsed"
        title={`Switch to ${LABELS[next]?.label || next}`}
      >
        <CurIcon className="h-4 w-4" />
      </button>
    );
  }

  return (
    <div className="px-3 mb-3" data-testid="workspace-switcher">
      <p className="text-[9px] uppercase tracking-[0.22em] text-ndor-ink-muted font-mono-ui mb-1.5">
        Workspace
      </p>
      <Select
        value={current}
        onValueChange={(v) => switchWorkspace(v)}
      >
        <SelectTrigger
          className="h-9 text-sm bg-ndor-surface-alt border-ndor-edge"
          data-testid="workspace-switcher-trigger"
        >
          <div className="flex items-center gap-2">
            <CurIcon className="h-4 w-4 text-ndor-ink-muted" />
            <SelectValue />
          </div>
        </SelectTrigger>
        <SelectContent>
          {allowedWorkspaces.map((ws) => {
            const meta = LABELS[ws];
            if (!meta) return null;
            const { Icon } = meta;
            return (
              <SelectItem
                key={ws}
                value={ws}
                data-testid={`workspace-switcher-option-${ws}`}
              >
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-ndor-ink-muted" />
                  <span>{meta.label}</span>
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}
